import React, { useState, useEffect } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import moment from 'moment';
import {
  useRequestAuthorizationMutation,
  useLazyGetLastCheckoutByRoomQuery,
} from '../redux/services/foauthorizationApi';
import { ShowToast } from '../Common/common';
import {
  validateAuthorizationRequest,
  buildAuthorizationPayload,
} from '../utils/foActionUtils';

import { usePhoneticText } from '../utils/transliterate/usePhoneticInput';
import T from '../Components/T';
import { tp } from '../utils/translateText';
interface RequestAuthorizationModalProps {
  visible: boolean;
  room: any;
  siblingRooms?: any[];
  onClose: () => void;
  onSuccess?: () => void;
}

const AUTH_TYPES = [
  { label: 'Add Grace Time (Hours)', value: 'GraceTime' },
  { label: 'Add Discount (Amount)', value: 'Discount' },
  { label: 'Remove Extra Bed', value: 'RemoveExtraBed' },
  { label: 'Reduce Tariff (New Rate)', value: 'ReduceTariff' },
  { label: 'Change Room Status', value: 'RoomStatus' },
  { label: 'Open to Roll Back (Rollback Checkout)', value: 'OpenToRollBack' },
  { label: 'Delete Check-in', value: 'DeleteCheckin' },
];

export const RequestAuthorizationModal: React.FC<RequestAuthorizationModalProps> = ({
  visible,
  room,
  siblingRooms = [],
  onClose,
  onSuccess,
}) => {
  const isOccupied = room?.rm_status === 'O';
  const defaultAuthType = isOccupied ? 'GraceTime' : 'RoomStatus';

  const [authType, setAuthType] = useState<string>(defaultAuthType);
  const [value, setValue] = useState<string>('');
  const ph = usePhoneticText();
  const [remarks, setRemarks] = useState<string>('');
  const [applyToAll, setApplyToAll] = useState<boolean>(false);

  const [requestAuth, { isLoading: isSubmitting }] = useRequestAuthorizationMutation();
  const [getLastCheckout, { data: lastCheckout, isFetching: isFetchingCheckout }] =
    useLazyGetLastCheckoutByRoomQuery();

  useEffect(() => {
    if (visible && room) {
      const initType = isOccupied ? 'GraceTime' : 'RoomStatus';
      setAuthType(initType);
      setValue(initType === 'RoomStatus' ? (room.rm_status === 'V' ? 'D' : 'V') : '');
      setRemarks('');
      setApplyToAll(false);
    }
  }, [visible, room, isOccupied]);

  const handleTypeChange = (newType: string) => {
    setAuthType(newType);
    if (newType === 'RoomStatus') {
      setValue(room?.rm_status === 'V' ? 'D' : 'V');
    } else {
      setValue('');
    }

    if (newType === 'OpenToRollBack' && room?.rm_id) {
      getLastCheckout(room.rm_id);
    }
  };

  if (!room) return null;

  const handleSubmit = async () => {
    const validation = validateAuthorizationRequest({
      authType,
      value,
      remarks,
      lastCheckout,
    });

    if (!validation.valid) {
      ShowToast(validation.error || 'Validation error', 'warning');
      return;
    }

    const payload = buildAuthorizationPayload({
      room,
      authType,
      value,
      remarks,
      applyToAll,
      lastCheckout,
    });

    try {
      await requestAuth(payload).unwrap();
      ShowToast('Authorization requested successfully', 'success');
      onSuccess?.();
      onClose();
    } catch (error: any) {
      const msg =
        error?.data?.message ||
        error?.data ||
        error?.message ||
        'Error requesting authorization';
      ShowToast(typeof msg === 'string' ? msg : 'Error requesting authorization', 'error');
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}>
      <View style={modalStyles.overlay}>
        <View style={modalStyles.container}>
          {/* Header */}
          <View style={modalStyles.header}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <FontAwesome name="gavel" size={20} color="#dc2626" />
              <Text style={modalStyles.title}><T>Request Authorization</T></Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              disabled={isSubmitting}
              style={modalStyles.closeButton}>
              <FontAwesome name="times" size={20} color="#6b7280" />
            </TouchableOpacity>
          </View>

          <ScrollView style={{ maxHeight: 460 }} keyboardShouldPersistTaps="handled">
            {/* Room Info Badge */}
            <View style={modalStyles.infoCard}>
              <View style={modalStyles.infoRow}>
                <Text style={modalStyles.infoLabel}><T>Target Room:</T></Text>
                <Text style={modalStyles.infoValueBold}>
                  {room.rm_name} {room.rt_name ? `(${room.rt_name})` : ''}
                </Text>
              </View>
              {room.ab_name ? (
                <View style={modalStyles.infoRow}>
                  <Text style={modalStyles.infoLabel}><T>Guest:</T></Text>
                  <Text style={modalStyles.infoValue}>{room.ab_name}</Text>
                </View>
              ) : null}
              {room.ci_id ? (
                <View style={modalStyles.infoRow}>
                  <Text style={modalStyles.infoLabel}><T>Folio No:</T></Text>
                  <Text style={modalStyles.infoValue}>#{room.ci_id}</Text>
                </View>
              ) : null}
            </View>

            {/* Type Picker */}
            <View style={modalStyles.formGroup}>
              <Text style={modalStyles.fieldLabel}><T>Authorization Type:</T></Text>
              <View style={modalStyles.pickerWrapper}>
                <Picker
                  selectedValue={authType}
                  onValueChange={handleTypeChange}
                  dropdownIconColor="#111827"
                  style={modalStyles.picker}>
                  {AUTH_TYPES.map(item => (
                    <Picker.Item
                      key={item.value}
                      label={item.label}
                      value={item.value}
                    />
                  ))}
                </Picker>
              </View>
            </View>

            {/* Contextual Info for OpenToRollBack */}
            {authType === 'OpenToRollBack' && (
              <View style={modalStyles.rollbackCard}>
                {isFetchingCheckout ? (
                  <View style={{ padding: 12, alignItems: 'center' }}>
                    <ActivityIndicator size="small" color="#dc2626" />
                    <Text style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      <T>Looking up last checkout bill...</T>
                    </Text>
                  </View>
                ) : lastCheckout ? (
                  <View>
                    <Text style={modalStyles.rollbackTitle}>
                      <T>Last Checkout Bill: #</T>{lastCheckout.co_no || lastCheckout.co_id}
                    </Text>
                    <View style={modalStyles.rollbackRow}>
                      <Text style={modalStyles.rollbackLabel}><T>Guest:</T></Text>
                      <Text style={modalStyles.rollbackValue}>
                        {lastCheckout.co_name || 'N/A'}
                      </Text>
                    </View>
                    <View style={modalStyles.rollbackRow}>
                      <Text style={modalStyles.rollbackLabel}><T>Bill Amount:</T></Text>
                      <Text style={modalStyles.rollbackValueBold}>
                        ₹{Number(lastCheckout.co_net || 0).toFixed(2)}
                      </Text>
                    </View>
                    <View style={modalStyles.rollbackRow}>
                      <Text style={modalStyles.rollbackLabel}><T>Checkout Time:</T></Text>
                      <Text style={modalStyles.rollbackValue}>
                        {lastCheckout.co_date
                          ? moment(lastCheckout.co_date).format('DD-MMM-YY HH:mm')
                          : '-'}
                      </Text>
                    </View>
                    <Text style={modalStyles.rollbackNotice}>
                      <T>Manager approval will restore this folio and return room to Occupied (O).</T>
                    </Text>
                  </View>
                ) : (
                  <Text style={modalStyles.rollbackError}>
                    <T>No previous checkout bill found for Room</T> {room.rm_name}.
                  </Text>
                )}
              </View>
            )}

            {/* Contextual Warning for DeleteCheckin */}
            {authType === 'DeleteCheckin' && (
              <View style={modalStyles.deleteWarningBox}>
                <FontAwesome name="exclamation-triangle" size={16} color="#d97706" />
                <Text style={modalStyles.deleteWarningText}>
                  <T>Requests manager approval to delete this check-in folio and release the room(s).</T>
                </Text>
              </View>
            )}

            {/* RoomStatus Picker */}
            {authType === 'RoomStatus' && (
              <View style={modalStyles.formGroup}>
                <Text style={modalStyles.fieldLabel}><T>New Room Status:</T></Text>
                <View style={modalStyles.pickerWrapper}>
                  <Picker
                    selectedValue={value}
                    onValueChange={v => setValue(v)}
                    dropdownIconColor="#111827"
                    style={modalStyles.picker}>
                    <Picker.Item label="Vacant (V)" value="V" />
                    <Picker.Item label="Dirty (D)" value="D" />
                    <Picker.Item label="Block (B)" value="B" />
                    <Picker.Item label="Maintenance (M)" value="M" />
                  </Picker>
                </View>
              </View>
            )}

            {/* Numeric Value Input for GraceTime / Discount / ReduceTariff */}
            {authType !== 'RemoveExtraBed' &&
              authType !== 'RoomStatus' &&
              authType !== 'OpenToRollBack' &&
              authType !== 'DeleteCheckin' && (
                <View style={modalStyles.formGroup}>
                  <Text style={modalStyles.fieldLabel}>
                    {authType === 'GraceTime'
                      ? 'Grace Period (Hours):'
                      : authType === 'Discount'
                      ? 'Discount Amount (₹):'
                      : 'New Tariff Rate (₹):'}
                  </Text>
                  <TextInput
                    style={modalStyles.textInput}
                    value={value}
                    onChangeText={setValue}
                    keyboardType="numeric"
                    placeholder={
                      authType === 'GraceTime' ? 'e.g. 2' : 'e.g. 500'
                    }
                    placeholderTextColor="#9ca3af"
                  />
                </View>
              )}

            {/* Sibling Rooms Checkbox */}
            {isOccupied &&
              siblingRooms.length > 1 &&
              authType !== 'GraceTime' &&
              authType !== 'RoomStatus' &&
              authType !== 'OpenToRollBack' &&
              authType !== 'DeleteCheckin' && (
                <TouchableOpacity
                  style={modalStyles.checkboxRow}
                  onPress={() => setApplyToAll(!applyToAll)}>
                  <FontAwesome
                    name={applyToAll ? 'check-square' : 'square-o'}
                    size={20}
                    color={applyToAll ? '#dc2626' : '#6b7280'}
                  />
                  <Text style={modalStyles.checkboxLabel}>
                    <T>Apply to all</T> {siblingRooms.length} <T>rooms in this Check-in (</T>
                    {siblingRooms.map(r => r.rm_name).join(', ')})
                  </Text>
                </TouchableOpacity>
              )}

            {/* Remarks Input */}
            <View style={modalStyles.formGroup}>
              <Text style={modalStyles.fieldLabel}><T>Remarks / Justification: *</T></Text>
              <TextInput
                style={[modalStyles.textInput, { height: 80, textAlignVertical: 'top' }]}
                value={remarks}
                onChangeText={(t) => setRemarks(ph.onChange(t))}
                onBlur={() => setRemarks(ph.onBlur(remarks))}
                placeholder={tp("Enter reason for authorization request...")}
                placeholderTextColor="#9ca3af"
                multiline
                numberOfLines={3}
              />
            </View>
          </ScrollView>

          {/* Action Buttons */}
          <View style={modalStyles.actionRow}>
            <TouchableOpacity
              onPress={onClose}
              disabled={isSubmitting}
              style={modalStyles.cancelBtn}>
              <Text style={modalStyles.cancelBtnText}><T>Cancel</T></Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleSubmit}
              disabled={
                isSubmitting ||
                (authType === 'OpenToRollBack' && (!lastCheckout || isFetchingCheckout))
              }
              style={[
                modalStyles.submitBtn,
                (isSubmitting ||
                  (authType === 'OpenToRollBack' && (!lastCheckout || isFetchingCheckout))) &&
                  modalStyles.submitBtnDisabled,
              ]}>
              {isSubmitting ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <>
                  <FontAwesome name="paper-plane" size={15} color="#ffffff" style={{ marginRight: 6 }} />
                  <Text style={modalStyles.submitBtnText}><T>Submit Request</T></Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const modalStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  container: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 18,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
    marginLeft: 8,
  },
  closeButton: {
    padding: 4,
  },
  infoCard: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: 10,
    marginBottom: 12,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
  },
  infoLabel: {
    fontSize: 13,
    color: '#64748b',
    fontWeight: '500',
  },
  infoValue: {
    fontSize: 13,
    color: '#1e293b',
  },
  infoValueBold: {
    fontSize: 13,
    color: '#0f172a',
    fontWeight: '700',
  },
  formGroup: {
    marginBottom: 12,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#334155',
    marginBottom: 6,
  },
  pickerWrapper: {
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  picker: {
    height: 48,
    color: '#0f172a',
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#0f172a',
    backgroundColor: '#ffffff',
  },
  rollbackCard: {
    backgroundColor: '#fef2f2',
    borderColor: '#fecaca',
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
  },
  rollbackTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#b91c1c',
    marginBottom: 6,
  },
  rollbackRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  rollbackLabel: {
    fontSize: 12,
    color: '#7f1d1d',
  },
  rollbackValue: {
    fontSize: 12,
    color: '#111827',
  },
  rollbackValueBold: {
    fontSize: 13,
    fontWeight: '700',
    color: '#b91c1c',
  },
  rollbackNotice: {
    fontSize: 11,
    color: '#991b1b',
    marginTop: 6,
    fontStyle: 'italic',
  },
  rollbackError: {
    fontSize: 13,
    color: '#dc2626',
    padding: 8,
    textAlign: 'center',
  },
  deleteWarningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fffbeb',
    borderColor: '#fde68a',
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
    gap: 8,
  },
  deleteWarningText: {
    fontSize: 12,
    color: '#92400e',
    flex: 1,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  checkboxLabel: {
    fontSize: 13,
    color: '#374151',
    flex: 1,
  },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 10,
  },
  cancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#475569',
  },
  submitBtn: {
    flexDirection: 'row',
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#dc2626',
    justifyContent: 'center',
    alignItems: 'center',
  },
  submitBtnDisabled: {
    opacity: 0.6,
  },
  submitBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#ffffff',
  },
});

export default RequestAuthorizationModal;
